import { useMemo, useState } from "react";

const agentStatusLabel = {
  approval_required: "승인 요청 중",
  succeeded: "완료",
  failed: "실패",
  blocked: "차단",
  running: "실행 중",
  repairing: "오류 수정 중",
  planning: "계획 수립 중",
  queued: "대기 중",
};

const eventLabel = {
  "approval.required": "승인 요청",
  "approval.accepted": "승인 완료",
  "approval.rejected": "승인 거절",
  "package.missing_module": "필요 패키지 감지",
  "package.install.completed": "패키지 설치 완료",
  "runner.completed": "실행 완료",
  "runner.started": "실행 시작",
  "planning.started": "계획 수립 시작",
  "planning.completed": "계획 수립 완료",
  "repair.started": "오류 수정 시작",
  "task.failed": "작업 실패",
  "safety.blocked": "안전 차단",
};

function renderAgentLogLine(item) {
  const detail = item?.detail || {};
  const label = eventLabel[item?.event] || item?.event || "작업 기록";

  if (item?.event === "package.install.completed") {
    const state = detail.returncode === 0 ? "성공" : "실패";
    return `${label}: ${detail.package || "-"} (${state})`;
  }
  if (item?.event === "approval.required" || item?.event === "approval.accepted") {
    return `${label}: ${detail.package || "-"}`;
  }
  if (item?.event === "runner.completed") {
    return `${label}: returncode ${detail.returncode}`;
  }
  return label;
}

function getAgentTaskGroup(status) {
  if (status === "approval_required") return "pending";
  if (["succeeded"].includes(status)) return "done";
  if (["failed", "blocked"].includes(status)) return "failed";
  if (["queued", "planning", "running", "repairing"].includes(status)) return "running";
  return "all";
}

function getPendingReason(task) {
  const pending = task?.pending_action || {};
  if (pending.reason) return pending.reason;
  if (pending.package) return `${pending.package} 패키지 설치 승인이 필요합니다.`;
  return "작업을 계속하려면 관리자 확인이 필요합니다.";
}

function RecentOverviewPanels({ overview, isSuperAdmin }) {
  return (
    <div className="dashboard-panels">
      <div className="dashboard-panel">
        <h3>최근 가입자</h3>
        {(overview?.recent_users || []).length === 0 ? (
          <p className="dashboard-muted">최근 가입자가 없습니다.</p>
        ) : (
          <ul>
            {(overview?.recent_users || []).map((item) => (
              <li key={`${item.userid}-${item.created_at}`}>
                <span>{item.name || item.userid}</span>
                <small>{item.company || "-"} · {item.is_approved ? "승인 완료" : "승인 대기"}</small>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="dashboard-panel">
        <h3>최근 질문</h3>
        {(overview?.recent_questions || []).length === 0 ? (
          <p className="dashboard-muted">최근 질문이 없습니다.</p>
        ) : (
          <ul>
            {(overview?.recent_questions || []).map((item, index) => (
              <li key={`${item.userid}-${item.created_at}-${index}`}>
                <span>비공개 질문 기록</span>
                <small>{item.name || item.userid || "사용자"} · {item.company || "-"} · {Number(item.content_length || 0)}자</small>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="dashboard-panel">
        <h3>{isSuperAdmin ? "회사별 사용 현황" : "회사 현황"}</h3>
        {(overview?.company_usage || []).length === 0 ? (
          <p className="dashboard-muted">표시할 회사 데이터가 없습니다.</p>
        ) : (
          <ul>
            {(overview?.company_usage || []).map((item) => (
              <li key={item.company || "unknown"}>
                <span>{item.company || "미지정"}</span>
                <small>회원 {item.total_users || 0}명 · 대기 {item.pending_users || 0}명</small>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function AgentPanel({ agentTasks, agentError, onAgentApprove, approvingTaskIds }) {
  const [filter, setFilter] = useState("all");
  const counts = useMemo(() => ({
    all: agentTasks.length,
    pending: agentTasks.filter((task) => getAgentTaskGroup(task.status) === "pending").length,
    running: agentTasks.filter((task) => getAgentTaskGroup(task.status) === "running").length,
    done: agentTasks.filter((task) => getAgentTaskGroup(task.status) === "done").length,
    failed: agentTasks.filter((task) => getAgentTaskGroup(task.status) === "failed").length,
  }), [agentTasks]);
  const filteredTasks = filter === "all" ? agentTasks : agentTasks.filter((task) => getAgentTaskGroup(task.status) === filter);

  return (
    <div className="dashboard-panel agent-work-panel full">
      <div className="agent-panel-header">
        <div>
          <h3>Agent 작업 현황</h3>
          <p className="dashboard-muted">승인 대기, 실행 중, 완료, 실패 작업을 구분해서 확인합니다.</p>
        </div>
      </div>

      {agentError && <div className="admin-error compact">{agentError}</div>}

      <div className="agent-filter-row">
        {[
          ["all", "전체"],
          ["pending", "승인 대기"],
          ["running", "실행 중"],
          ["done", "완료"],
          ["failed", "실패/차단"],
        ].map(([id, label]) => (
          <button key={id} className={filter === id ? "active" : ""} onClick={() => setFilter(id)}>
            {label} <span>{counts[id] || 0}</span>
          </button>
        ))}
      </div>

      {filteredTasks.length === 0 ? (
        <p className="dashboard-muted">최근 Agent 작업이 없습니다.</p>
      ) : (
        <ul>
          {filteredTasks.map((task) => {
            const isApproving = approvingTaskIds.includes(task.task_id);
            const installPackage = task.pending_action?.package || (task.installed_packages || []).join(", ");
            return (
              <li key={task.task_id} className={`agent-task-row status-${task.status}`}>
                <div className="agent-task-topline">
                  <span>{String(task.instruction || "").slice(0, 120)}</span>
                  <strong>{isApproving ? "승인 처리 중" : agentStatusLabel[task.status] || task.status}</strong>
                </div>
                <small>작업 ID: {task.task_id || "-"}</small>
                {installPackage && <small>관련 패키지: {installPackage}</small>}

                {task.status === "approval_required" && (
                  <div className="agent-pending-box">
                    <strong>승인 전 확인</strong>
                    <p>{getPendingReason(task)}</p>
                    {task.pending_action?.module && <small>감지된 모듈: {task.pending_action.module}</small>}
                    {task.pending_action?.package && <small>설치 요청: {task.pending_action.package}</small>}
                  </div>
                )}

                {task.status === "approval_required" && (
                  <span className="agent-action-row">
                    <button className="admin-approve-button" disabled={isApproving} onClick={() => onAgentApprove(task.task_id, true)}>
                      {isApproving ? "승인 처리 중" : "승인"}
                    </button>
                    <button className="admin-hold-button" disabled={isApproving} onClick={() => onAgentApprove(task.task_id, false)}>
                      거절
                    </button>
                  </span>
                )}

                {(task.installed_packages || []).length > 0 && (
                  <small className="agent-installed">설치 완료: {(task.installed_packages || []).join(", ")}</small>
                )}

                {task.stdout && <pre className="agent-output-preview">{String(task.stdout).slice(0, 1200)}</pre>}
                {task.stderr && task.status !== "approval_required" && <pre className="agent-output-preview error">{String(task.stderr).slice(0, 1200)}</pre>}

                {(task.logs || []).length > 0 && (
                  <details className="agent-log-details">
                    <summary>처리 로그 보기</summary>
                    <ol>
                      {(task.logs || []).slice(-8).map((log, index) => (
                        <li key={`${task.task_id}-log-${index}`}>{renderAgentLogLine(log)}</li>
                      ))}
                    </ol>
                  </details>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function AuditLogPanel({ auditLogs }) {
  return (
    <div className="dashboard-panel full">
      <h3>최근 관리 기록</h3>
      {auditLogs.length === 0 ? (
        <p className="dashboard-muted">최근 관리 기록이 없습니다.</p>
      ) : (
        <ul>
          {auditLogs.map((item, index) => (
            <li key={`${item.action}-${item.created_at}-${index}`}>
              <span>{item.action} {item.target_userid ? `· ${item.target_userid}` : ""}</span>
              <small>{item.actor_userid || "-"} · {item.created_at || ""}</small>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DashboardPanels({ section = "overview", overview, auditLogs, agentTasks, agentError, isSuperAdmin, onAgentApprove, approvingTaskIds = [] }) {
  if (section === "agent") {
    return <AgentPanel agentTasks={agentTasks} agentError={agentError} onAgentApprove={onAgentApprove} approvingTaskIds={approvingTaskIds} />;
  }
  if (section === "logs") {
    return <AuditLogPanel auditLogs={auditLogs} />;
  }
  return <RecentOverviewPanels overview={overview} isSuperAdmin={isSuperAdmin} />;
}

export default DashboardPanels;
